import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "../../../Components/Table/Table";
import {
  getPendingEmployees,
  approveEmployee,
  rejectEmployee,
} from "../../../api/register";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import toast from "react-hot-toast";

const AdminPendingList = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      setLoading(true);
      const res = await getPendingEmployees();

      if (Array.isArray(res.data)) {
        setEmployees(res.data);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      toast.error("Failed to fetch pending employees");
    } finally {
      setLoading(false);
    }
  };

  const confirmApprove = async () => {
    try {
      await approveEmployee(selectedId);
      toast.success("Employee Approved ✅");
      setShowApproveModal(false);
      fetchPending();
    } catch {
      toast.error("Approval failed");
    }
  };

  const confirmReject = async () => {
    if (!rejectReason.trim()) {
      return toast.error("Please enter reject reason");
    }

    try {
      await rejectEmployee(selectedId, { reason: rejectReason });
      toast.success("Employee Rejected ❌");

      setShowRejectModal(false);
      setRejectReason("");
      fetchPending();
    } catch {
      toast.error("Rejection failed");
    }
  };

  // ✅ Table Columns
  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        Cell: ({ row }) => <span>{row.index + 1}</span>,
      },
      {
        Header: "Full Name",
        Cell: ({ row }) => (
          <span>
            {row.original.firstName} {row.original.lastName}
          </span>
        ),
      },
      {
        Header: "Employee ID",
        accessor: "employeeId",
      },
      {
        Header: "Official Email",
        accessor: "officialmailid",
      },
      {
        Header: "Designation",
        accessor: "designation",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          let color = "";

          if (value === "pending") color = "#f39c12";
          if (value === "approved") color = "#2ecc71";
          if (value === "rejected") color = "#e74c3c";

          return (
            <span
              style={{
                backgroundColor: color,
                color: "#fff",
                padding: "4px 10px",
                borderRadius: "20px",
                fontSize: "12px",
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const emp = row.original;

          return (
            <div className="action-icons d-flex gap-3">
              <VisibilityIcon
                style={{ cursor: "pointer", color: "#1976d2" }}
                onClick={() => navigate(`/admin/confirm/${emp._id}`)}
              />

              {/* ✅ Show Approve only if NOT approved */}
              {emp.status !== "approved" && (
                <CheckCircleIcon
                  style={{ cursor: "pointer", color: "green" }}
                  onClick={() => {
                    setSelectedId(emp._id);
                    setShowApproveModal(true);
                  }}
                />
              )}

              {/* ❌ Show Reject only if NOT rejected */}
              {emp.status !== "rejected" && (
                <CancelIcon
                  style={{ cursor: "pointer", color: "red" }}
                  onClick={() => {
                    setSelectedId(emp._id);
                    setShowRejectModal(true);
                  }}
                />
              )}
            </div>
          );
        },
      }
    ],
    [navigate]
  );

  // ✅ Search Filter
  const data = useMemo(() => {
    return employees.filter((emp) => {
      const text = search.toLowerCase();

      return (
        emp?.firstName?.toLowerCase().includes(text) ||
        emp?.lastName?.toLowerCase().includes(text) ||
        emp?.officialmailid?.toLowerCase().includes(text) ||
        emp?.employeeId?.toLowerCase().includes(text)
      );
    });
  }, [employees, search]);

  return (
    <>
      <div className="container-admin">
        <div className="top-bar d-flex ac-jb">
          <h2 className="f2">Pending Employee Approvals</h2>
        </div>

        <input
          type="text"
          placeholder="Search employee..."
          className="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <p>Loading...</p>
        ) : (
          <Table columns={columns} data={data} />
        )}
      </div>

      {showApproveModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Approve Employee</h3>
            <p>Are you sure you want to approve?</p>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowApproveModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn-approve"
                onClick={confirmApprove}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}


      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Reject Employee</h3>

            <textarea
              className="reject-textarea"
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowRejectModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn-reject"
                onClick={confirmReject}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

    </>

  );
};

export default AdminPendingList;